"use client";

import { useCallback, useEffect, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";

import { DEFAULT_WORKSPACE_TABS } from "@/components/workspaces/workspace-tabs.config";
import { WorkspaceTabs } from "@/components/workspaces/workspace-tabs";
import { useWorkspaceRouteNamespace } from "@/components/workspaces/workspace-route-context";
import {
  useSetWorkspaceTabRailActivePosition,
  type WorkspaceTabRailActivePosition,
} from "@/components/workspaces/workspace-tab-rail-position-context";
import { resolveWorkspaceRoute } from "@/config/workspace-routes";
import { useAvailableWorkspaceTabs } from "@/hooks/useNamespaceFeatureFlags";
import { useActiveNamespaceSlug } from "@/hooks/useNamespaces";
import { useWorkspaceTabsStore } from "@/state/workspace-tabs-store";
import type { NamespaceId } from "@/types/namespaces";

const PINNED_TAB_IDS: NamespaceId[] = ["task-list"];
const CLOSABLE_TAB_IDS: NamespaceId[] = DEFAULT_WORKSPACE_TABS.map((tab) => tab.id).filter(
  (id) => !PINNED_TAB_IDS.includes(id),
);
const CLOSABLE_TAB_ID_SET = new Set<NamespaceId>(CLOSABLE_TAB_IDS);

type WorkspaceTabRailProps = {
  activeId: NamespaceId;
};

export function WorkspaceTabRail({ activeId }: WorkspaceTabRailProps) {
  const router = useRouter();
  const pathname = usePathname();
  const routeNamespace = useWorkspaceRouteNamespace();
  const ambientSlug = useActiveNamespaceSlug();
  const activeSlug = routeNamespace?.slug ?? ambientSlug;
  const closedTabs = useWorkspaceTabsStore((state) => state.closedTabs);
  const closeTab = useWorkspaceTabsStore((state) => state.closeTab);
  const reopenTab = useWorkspaceTabsStore((state) => state.reopenTab);

  // Entering a route opens its tab, including direct links with saved closed tabs.
  // Do not depend on closedTabs: closing the current tab must still navigate away.
  useEffect(() => { reopenTab(activeId); }, [activeId, reopenTab]);
  // Feature-gated tabs (ideas / changes / audit) are absent from this list
  // when their feature is disabled for the active namespace.
  const availableTabs = useAvailableWorkspaceTabs(routeNamespace?.namespaceId);

  const visibleTabs = useMemo(
    () => availableTabs.filter((tab) => tab.id === activeId || !closedTabs.includes(tab.id)),
    [activeId, availableTabs, closedTabs],
  );

  const fallbackTabId = useMemo(() => {
    const fallback = availableTabs.find((tab) => !closedTabs.includes(tab.id));
    return fallback?.id ?? availableTabs[0]?.id;
  }, [availableTabs, closedTabs]);

  const resolvedActiveId = useMemo(() => {
    if (visibleTabs.some((tab) => tab.id === activeId)) {
      return activeId;
    }

    return fallbackTabId;
  }, [activeId, fallbackTabId, visibleTabs]);

  const activePosition = useMemo<WorkspaceTabRailActivePosition>(() => {
    if (visibleTabs.length === 0) {
      return null;
    }
    if (visibleTabs.length === 1) {
      return "only";
    }
    const index = visibleTabs.findIndex((tab) => tab.id === resolvedActiveId);
    if (index === -1) {
      return null;
    }
    if (index === 0) {
      return "first";
    }
    if (index === visibleTabs.length - 1) {
      return "last";
    }
    return "middle";
  }, [resolvedActiveId, visibleTabs]);

  const setActivePosition = useSetWorkspaceTabRailActivePosition();
  useEffect(() => {
    setActivePosition(activePosition);
  }, [activePosition, setActivePosition]);

  const handleSelectTab = useCallback(
    (tabId: NamespaceId) => {
      const destination = resolveWorkspaceRoute(tabId, activeSlug ?? undefined);
      if (!destination || destination === pathname) {
        return;
      }

      router.push(destination);
    },
    [activeSlug, pathname, router],
  );

  const handleCloseTab = useCallback(
    (tabId: NamespaceId) => {
      if (!CLOSABLE_TAB_ID_SET.has(tabId)) {
        return;
      }

      const nextClosedTabs = new Set(closedTabs);
      nextClosedTabs.add(tabId);
      closeTab(tabId);

      if (resolvedActiveId === tabId) {
        const fallback = availableTabs.find((tab) => !nextClosedTabs.has(tab.id));
        if (fallback) {
          handleSelectTab(fallback.id);
        }
      }
    },
    [availableTabs, closedTabs, closeTab, handleSelectTab, resolvedActiveId],
  );

  const renderedTabs = visibleTabs.length > 0 ? visibleTabs : availableTabs;

  return (
    // Below `lg:` the workspace switcher in the TopBar (hamburger menu) covers
    // navigation, so the rail is hidden on mobile + tablet portrait to reclaim
    // vertical space. The full row stays at lg:+ where it's the primary nav.
    <div className="workspace-tab-rail hidden w-full min-w-0 lg:block">
      <WorkspaceTabs
        tabs={renderedTabs}
        active={resolvedActiveId}
        onSelectTab={handleSelectTab}
        onCloseTab={handleCloseTab}
        closableIds={CLOSABLE_TAB_IDS}
      />
    </div>
  );
}

export default WorkspaceTabRail;
